import { apiClient } from '@shared/api';
import type { ApiResponse } from '@shared/types';
import type {
  CacheHealthDto,
  ReferenceDataDto,
  SoReferenceRecordType,
} from '../types/salesOrder.types';

// FO-SOI-3: cached reference-data lookups (city/town + state/province). Both are
// cached server-side, so the FE just calls the endpoints; no need for a local
// cache layer.
class ReferenceDataService {
  async getCityTowns(): Promise<ReferenceDataDto[]> {
    const { data } = await apiClient.get<ApiResponse<ReferenceDataDto[]>>('/sales-orders/city-towns');
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to load city/towns');
  }

  async getStateProvinces(): Promise<ReferenceDataDto[]> {
    const { data } = await apiClient.get<ApiResponse<ReferenceDataDto[]>>('/sales-orders/state-provinces');
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to load states/provinces');
  }

  async getTerms(): Promise<ReferenceDataDto[]> {
    const { data } = await apiClient.get<ApiResponse<ReferenceDataDto[]>>('/sales-orders/terms');
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to load terms');
  }

  async getSchedules(): Promise<ReferenceDataDto[]> {
    const { data } = await apiClient.get<ApiResponse<ReferenceDataDto[]>>('/sales-orders/schedules');
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to load schedules');
  }

  // Per-line Price Level options (Base / Average) for the line-items table.
  async getPriceLevels(): Promise<ReferenceDataDto[]> {
    const { data } = await apiClient.get<ApiResponse<ReferenceDataDto[]>>('/sales-orders/price-levels');
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to load price levels');
  }

  // 86d39ekrd — SO-owned admin force-sync + cache-health (shared master-data admin
  // endpoints, gated server-side by quotations.sync_master_data). Kept here rather
  // than importing the quotation-intake service so SO owns its own sync surface.
  async forceSync(recordType: SoReferenceRecordType): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<unknown>>(
      '/admin/cache/force-sync',
      { recordType },
      { timeout: 120000 },
    );
    if (!data.success) throw new Error(data.message || 'Force sync failed');
  }

  async getCacheHealth(): Promise<CacheHealthDto[]> {
    const { data } = await apiClient.get<ApiResponse<CacheHealthDto[]>>('/admin/cache/health');
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to load cache health');
  }
}

export const referenceDataService = new ReferenceDataService();
